import React, { JSX, useCallback, useEffect, useState } from 'react'
import { ArrowPathIcon } from '@heroicons/react/24/outline'
import { LoadingSpinner } from 'components/Loading'
import { ErrorInline } from 'components/ErrorInline'
import {
  ProviderBrandIcon,
  TWITCH_BRAND_ICON_SRC,
  YOUTUBE_BRAND_ICON_SRC
} from 'components/ProviderBrandIcon'
import { useAuth } from 'context/user'
import { graphqlRequest } from 'lib/helpers'
import {
  TriviaLeaderboard,
  TriviaLeaderboardRow,
  TriviaQuestionStat
} from 'types/api'
import getTriviaLeaderboardQuery from 'graphql/getTriviaLeaderboard.graphql'

const SectionCard = ({
  title,
  children,
  action
}: { title: string, children: React.ReactNode, action?: React.ReactNode }): JSX.Element => (
  <section className='rounded-xl border border-white/5 bg-dm-bg-secondary p-5'>
    <div className='mb-4 flex items-center justify-between'>
      <h3 className='text-base font-semibold text-dm-text-primary'>{title}</h3>
      {action}
    </div>
    {children}
  </section>
)

const includeStaffStorageKey = (eventId: string): string =>
  `sp:triviaLeaderboardIncludeStaff:${eventId}`

const readIncludeStaff = (eventId: string): boolean => {
  try {
    return globalThis.localStorage.getItem(includeStaffStorageKey(eventId)) !== '0'
  } catch {
    return true
  }
}

const writeIncludeStaff = (eventId: string, value: boolean): void => {
  try {
    globalThis.localStorage.setItem(includeStaffStorageKey(eventId), value ? '1' : '0')
  } catch {

  }
}

const StaffTag = ({ row }: { row: TriviaLeaderboardRow }): JSX.Element | null => {
  if (!row.isBroadcaster && !row.isModerator) return null
  return (
    <span className='shrink-0 rounded-full bg-white/5 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-dm-text-accent ring-1 ring-inset ring-white/10'>
      {row.isBroadcaster ? 'Broadcaster' : 'Mod'}
    </span>
  )
}

const LeaderboardTable = ({ title, iconSrc, rows, emptyCopy }: {
  title: string
  iconSrc: string
  rows: TriviaLeaderboardRow[]
  emptyCopy: string
}): JSX.Element => (
  <div className='rounded-lg border border-white/5 bg-dm-bg-accent/30 p-3'>
    <div className='mb-2 flex items-center gap-2 text-sm font-medium text-dm-text-primary'>
      <ProviderBrandIcon src={iconSrc} size='sm' />
      {title}
    </div>
    {rows.length === 0
      ? <p className='text-xs text-dm-text-secondary'>{emptyCopy}</p>
      : (
        <ul className='space-y-1'>
          {rows.map((r, i) => (
            <li key={`${r.rank}-${i}`} className='flex items-center justify-between gap-2 text-sm'>
              <span className='flex min-w-0 items-center gap-2'>

                <span className='w-6 shrink-0 text-right text-[11px] tabular-nums text-dm-text-accent'>{r.rank}</span>
                <span className='truncate text-dm-text-secondary'>{r.displayName}</span>
                <StaffTag row={r} />
              </span>
              <span className='shrink-0 tabular-nums text-dm-text-accent'>
                {r.correct} / {r.answered}
              </span>
            </li>
          ))}
        </ul>
        )}
  </div>
)

const QuestionStatLine = ({ stat }: { stat: TriviaQuestionStat }): JSX.Element => (
  <li className='rounded-md border border-white/5 bg-dm-bg-accent/20 px-3 py-2'>
    <div className='mb-1 truncate text-sm text-dm-text-primary' title={stat.question}>{stat.question}</div>
    <div className='flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-dm-text-accent'>
      <span className='inline-flex items-center gap-1.5'>
        <ProviderBrandIcon src={TWITCH_BRAND_ICON_SRC} size='sm' />
        {stat.twitchPct === null
          ? 'No answers'
          : `${stat.twitchPct}% got it right (${stat.twitchCorrect} / ${stat.twitchAnswered})`}
      </span>
      <span className='inline-flex items-center gap-1.5'>
        <ProviderBrandIcon src={YOUTUBE_BRAND_ICON_SRC} size='sm' />

        {stat.youtubeRemoved
          ? 'removed'
          : stat.youtubePct === null
            ? 'No answers'
            : `${stat.youtubePct}% got it right (${stat.youtubeCorrect} / ${stat.youtubeAnswered})`}
      </span>
    </div>
  </li>
)

export const TriviaLeaderboardPanel = ({ eventId, reloadSignal = 0 }: {
  eventId: string
  reloadSignal?: number
}): JSX.Element => {
  const { auth0Token } = useAuth()
  const [includeStaff, setIncludeStaff] = useState<boolean>(() => readIncludeStaff(eventId))
  const [board, setBoard] = useState<TriviaLeaderboard | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async (): Promise<void> => {
    if (auth0Token === undefined) return
    setBusy(true)
    setError(null)
    try {
      const { leaderboard } = await graphqlRequest<{ leaderboard: TriviaLeaderboard | null }>(
        getTriviaLeaderboardQuery, { eventId, includeStaff }, auth0Token
      )
      setBoard(leaderboard)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load the leaderboard')
    } finally {
      setBusy(false)
    }
  }, [auth0Token, eventId, includeStaff])

  useEffect(() => { void load() }, [load, reloadSignal])

  const toggleStaff = (value: boolean): void => {
    setIncludeStaff(value)
    writeIncludeStaff(eventId, value)
  }

  return (
    <SectionCard
      title='Leaderboards'
      action={
        <button
          type='button'
          onClick={() => { void load() }}
          disabled={busy}
          title='Refresh leaderboards'
          className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-2.5 py-1.5 text-xs text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:opacity-60'
        >
          {busy ? <LoadingSpinner /> : <ArrowPathIcon className='size-3.5' aria-hidden='true' />}
          Refresh
        </button>
      }
    >
      <label className='mb-3 flex items-center gap-2 text-xs text-dm-text-secondary'>
        <input
          type='checkbox'
          checked={includeStaff}
          onChange={(e) => toggleStaff(e.target.checked)}
          className='size-4 rounded border-white/20 bg-dm-bg-accent/60 text-brand focus:ring-brand'
        />
        Include broadcaster and moderators (Twitch board)
      </label>
      {error !== null && (
        <div className='mb-3'>
          <ErrorInline onDismiss={() => setError(null)}>{error}</ErrorInline>
        </div>
      )}
      {board === null
        ? (
          <p className='rounded-md border border-dashed border-white/10 px-3 py-6 text-center text-xs text-dm-text-secondary'>
            {busy ? 'Loading the leaderboards…' : 'No leaderboard data yet.'}
          </p>
          )
        : board.revealedQuestionCount === 0
          ? (
            <p className='rounded-md border border-dashed border-white/10 px-3 py-6 text-center text-xs text-dm-text-secondary'>
              The leaderboard fills in as questions are revealed. Only revealed
              questions score.
            </p>
            )
          : (
            <>
              <div className='grid grid-cols-1 gap-3 md:grid-cols-2'>
                <LeaderboardTable
                  title='Twitch'
                  iconSrc={TWITCH_BRAND_ICON_SRC}
                  rows={board.twitch}
                  emptyCopy='No Twitch answers on revealed questions yet.'
                />
                <LeaderboardTable
                  title='YouTube'
                  iconSrc={YOUTUBE_BRAND_ICON_SRC}
                  rows={board.youtube}
                  emptyCopy='No YouTube answers on revealed questions yet.'
                />
              </div>
              <p className='mt-2 text-[10px] text-dm-text-accent'>
                Scores show correct / answered across {board.revealedQuestionCount} revealed
                question{board.revealedQuestionCount === 1 ? '' : 's'}. Ties share a rank.
                The two boards are separate by design and are never combined.
              </p>
              {board.questions.length > 0 && (
                <div className='mt-4'>
                  <div className='mb-2 text-xs font-medium uppercase tracking-wider text-dm-text-accent'>
                    Per question
                  </div>
                  <ul className='space-y-1.5'>
                    {board.questions.map((q) => <QuestionStatLine key={q.triviaId} stat={q} />)}
                  </ul>
                </div>
              )}
            </>
            )}
    </SectionCard>
  )
}
